import React, { useState, useEffect } from 'react';
import { 
    Modal,
    Box, 
    Typography, 
    Button, 
    Card, 
    CardContent, 
    Chip, 
    IconButton, 
    Grid, 
    CircularProgress, 
    Alert,
    Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import AddFIXSessionModal from './AddFIXSessionModal';
import EditFIXSessionModal from './EditFIXSessionModal';
import { getFIXSession, deleteFIXSession } from '../../api/fixSessionApi';
import { toast } from 'react-toastify';
import { useAuth } from '../../provider/authProvider';

const LPConfigurationModal = ({ open, onClose, lp }) => {
    const { hasPermission } = useAuth();
    const [fixSession, setFixSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddFIXSession, setShowAddFIXSession] = useState(false);
    const [showEditFIXSession, setShowEditFIXSession] = useState(false);

    // Fetch FIX session from API
    const fetchFIXSession = async () => {
        if (!lp || !lp.id) return;
        
        // Prevent multiple simultaneous calls
        if (loading) {
            console.log('FIX session fetch already in progress, skipping...');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            console.log('Fetching FIX session for LP:', lp.id, lp.name);
            const response = await getFIXSession(token, lp.id);
            console.log('API Response:', response);
            console.log('API Response Status:', response?.status);
            console.log('API Response Data:', response?.data);
            
            // Handle null response (no FIX session exists)
            if (response === null) {
                console.log('No FIX session configuration found - this is normal');
                setFixSession(null);
                setError(null);
                return;
            }
            
            // Extract the actual FIX session data from the response
            const fixSessionData = response?.data || response;
            console.log('Extracted FIX session data:', fixSessionData);
            console.log('Is fixSessionData an array?', Array.isArray(fixSessionData));
            console.log('fixSessionData length:', Array.isArray(fixSessionData) ? fixSessionData.length : 'Not an array');
            
            // Handle array of FIX sessions by combining them into a single object
            if (Array.isArray(fixSessionData) && fixSessionData.length > 0) {
                const combinedSession = {
                    quoteSession: null,
                    tradeSession: null,
                    generalSettings: null
                };
                
                // Combine data from all sessions
                fixSessionData.forEach(session => {
                    if (session.quoteSession) {
                        combinedSession.quoteSession = session.quoteSession;
                    }
                    if (session.tradeSession) {
                        combinedSession.tradeSession = session.tradeSession;
                    }
                    if (session.generalSettings) {
                        combinedSession.generalSettings = session.generalSettings;
                    }
                });
                
                console.log('Combined FIX session data:', combinedSession);
                setFixSession(combinedSession);
            } else {
                setFixSession(fixSessionData);
            }
        } catch (error) {
            console.error('Error fetching FIX session:', error);
            
            // Check if it's a 404 (no FIX session exists) - this is normal, don't show error
            if (error.response?.status === 404 || error.message?.includes('404')) {
                console.log('No FIX session configuration found - this is normal');
                setFixSession(null);
                setError(null); // Clear any previous errors
                // Don't show error toast for 404 - it's expected when no session exists
            } else {
                // Only show error for actual errors (not 404)
                setError(error.message || 'Failed to fetch FIX session configuration');
                toast.error('Failed to load FIX session configuration');
            }
        } finally {
            setLoading(false);
        }
    };

    // Load FIX session when modal opens
    useEffect(() => {
        if (open && lp) {
            fetchFIXSession();
        }
    }, [open, lp]);

    const handleAddFIXSession = () => {
        setShowAddFIXSession(true);
    };

    const handleEditFIXSession = () => {
        setShowEditFIXSession(true);
    };

    const handleDeleteFIXSession = async () => {
        if (window.confirm(`Are you sure you want to delete ALL FIX session configurations for "${lp?.name}"? This action cannot be undone.`)) {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    toast.error('Authentication token not found');
                    return;
                }

                console.log('Deleting FIX session for LP:', lp.id, lp.name);
                const result = await deleteFIXSession(token, lp.id);
                console.log('Delete result:', result);
                
                toast.success(`FIX session configuration for "${lp?.name}" deleted successfully!`);
                
                // Clear the fixSession state immediately
                setFixSession(null);
                
                // Wait a moment for the backend to process the deletion
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Refresh FIX session data
                console.log('Refreshing FIX session data after deletion...');
                await fetchFIXSession();
                
            } catch (error) {
                console.error('Error deleting FIX session:', error);
                toast.error(`Failed to delete FIX session configuration: ${error.message || 'Unknown error'}`);
            }
        }
    };

    const handleAddFIXSessionSuccess = (newSession) => {
        console.log('FIX session created successfully:', newSession);
        fetchFIXSession(); // Refresh the session
    };

    const handleEditFIXSessionSuccess = (updatedSession) => {
        console.log('FIX session updated successfully:', updatedSession);
        fetchFIXSession(); // Refresh the session
    };

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'enabled':
            case 'connected':
                return '#1de9b6';
            case 'disabled':
            case 'disconnected':
                return '#ff6b6b';
            case 'connecting':
                return '#ffd600';
            default:
                return '#bfc9db';
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="lp-configuration-modal-title"
            aria-describedby="lp-configuration-modal-description"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
                maxWidth: 1200,
                maxHeight: '90vh',
                bgcolor: 'rgba(24, 31, 44, 0.95)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
            {/* Header */}
            <Box sx={{
                p: 3,
                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                background: 'rgba(6, 182, 212, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <SettingsIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
                    <Box>
                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                            LP Configuration - {lp?.name?.toUpperCase() || 'UNKNOWN'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                            Manage FIX session configuration for this liquidity provider
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchFIXSession}
                        disabled={loading}
                        sx={{
                            color: '#bfc9db',
                            borderColor: 'rgba(6, 182, 212, 0.3)',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                background: 'rgba(6, 182, 212, 0.1)'
                            }
                        }}
                    >
                        Refresh
                    </Button>
                    {(!fixSession || (!fixSession.quoteSession && !fixSession.tradeSession)) && hasPermission('Settings.LPManagement.LPConfiguration.Create') && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddFIXSession}
                            sx={{
                                background: '#9c27b0',
                                color: '#fff',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                    background: '#7b1fa2'
                                }
                            }}
                        >
                            Create FIX Session
                        </Button>
                    )}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#fff',
                            '&:hover': {
                                background: 'rgba(255, 255, 255, 0.1)',
                                color: '#bfc9db'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress sx={{ color: '#9c27b0' }} />
                        <Typography variant="body1" sx={{ color: '#fff', ml: 2 }}>
                            Loading FIX session configuration...
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            mb: 3,
                            background: 'rgba(211, 47, 47, 0.1)',
                            border: '1px solid #d32f2f',
                            color: '#fff',
                            '& .MuiAlert-icon': { color: '#ff6b6b' }
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* FIX Session Configuration */}
                {!loading && !error && (
                    <>
                        {fixSession && (fixSession.quoteSession || fixSession.tradeSession) ? (
                            <Grid container spacing={3}>
                                {/* General Information */}
                                <Grid item xs={12}>
                                    <Card sx={{
                                        background: 'rgba(24, 31, 44, 0.8)',
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        borderRadius: 2
                                    }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                                                    FIX Session Configuration
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {hasPermission('Settings.LPManagement.LPConfiguration.Edit') && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<EditIcon />}
                                                        onClick={handleEditFIXSession}
                                                        sx={{
                                                            color: '#00cfff',
                                                            borderColor: '#00cfff',
                                                            textTransform: 'none',
                                                            '&:hover': {
                                                                borderColor: '#1de9b6',
                                                                background: 'rgba(0, 207, 255, 0.1)'
                                                            }
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    )}
                                                    {hasPermission('Settings.LPManagement.LPConfiguration.Delete') && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={handleDeleteFIXSession}
                                                        sx={{
                                                            color: '#ff6b6b',
                                                            borderColor: '#ff6b6b',
                                                            textTransform: 'none',
                                                            '&:hover': {
                                                                borderColor: '#ff5252',
                                                                background: 'rgba(255, 107, 107, 0.1)'
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                            
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2" sx={{ color: '#bfc9db', mb: 1 }}>
                                                            LP ID:
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                                                            {fixSession.lpId}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2" sx={{ color: '#bfc9db', mb: 1 }}>
                                                            Status:
                                                        </Typography>
                                                        <Chip
                                                            label={fixSession.status || 'Unknown'}
                                                            size="small"
                                                            sx={{
                                                                background: getStatusColor(fixSession.status),
                                                                color: '#000',
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2" sx={{ color: '#bfc9db', mb: 1 }}>
                                                            Created At:
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: '#fff' }}>
                                                            {fixSession.createdAt ? new Date(fixSession.createdAt).toLocaleString() : 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2" sx={{ color: '#bfc9db', mb: 1 }}>
                                                            Last Updated:
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: '#fff' }}>
                                                            {fixSession.lastUpdated ? new Date(fixSession.lastUpdated).toLocaleString() : 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Quote Session */}
                                {fixSession.quoteSession && (
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{
                                            background: 'rgba(24, 31, 44, 0.8)',
                                            border: '1px solid rgba(0, 207, 255, 0.2)',
                                            borderRadius: 2
                                        }}>
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <CheckCircleIcon sx={{ color: '#00cfff', fontSize: 20 }} />
                                                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                                                        Quote Session
                                                    </Typography>
                                                </Box>
                                                
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Session ID: {fixSession.quoteSession.sessionId}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Host: {fixSession.quoteSession.host}:{fixSession.quoteSession.port}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Sender: {fixSession.quoteSession.senderCompId}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Target: {fixSession.quoteSession.targetCompId}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Username: {fixSession.quoteSession.username}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        SSL: {fixSession.quoteSession.sslEnabled ? 'Enabled' : 'Disabled'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Heartbeat: {fixSession.quoteSession.heartbeatInterval}s
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Chip
                                                        label={fixSession.quoteSession.status || 'Unknown'}
                                                        size="small"
                                                        sx={{
                                                            background: getStatusColor(fixSession.quoteSession.status),
                                                            color: '#000',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Trade Session */}
                                {fixSession.tradeSession && (
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{
                                            background: 'rgba(24, 31, 44, 0.8)',
                                            border: '1px solid rgba(29, 233, 182, 0.2)',
                                            borderRadius: 2
                                        }}>
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <CheckCircleIcon sx={{ color: '#1de9b6', fontSize: 20 }} />
                                                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                                                        Trade Session
                                                    </Typography>
                                                </Box>
                                                
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Session ID: {fixSession.tradeSession.sessionId}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Host: {fixSession.tradeSession.host}:{fixSession.tradeSession.port}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Sender: {fixSession.tradeSession.senderCompId}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Target: {fixSession.tradeSession.targetCompId}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Username: {fixSession.tradeSession.username}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        SSL: {fixSession.tradeSession.sslEnabled ? 'Enabled' : 'Disabled'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                        Heartbeat: {fixSession.tradeSession.heartbeatInterval}s
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Chip
                                                        label={fixSession.tradeSession.status || 'Unknown'}
                                                        size="small"
                                                        sx={{
                                                            background: getStatusColor(fixSession.tradeSession.status),
                                                            color: '#000',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 300,
                                textAlign: 'center'
                            }}>
                                <ErrorIcon sx={{ fontSize: 64, color: '#bfc9db', mb: 2 }} />
                                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                                    No FIX Session Configuration
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#bfc9db', mb: 3 }}>
                                    This liquidity provider doesn't have a FIX session configuration yet.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddFIXSession}
                                    sx={{
                                        background: '#9c27b0',
                                        color: '#fff',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        '&:hover': {
                                            background: '#7b1fa2'
                                        }
                                    }}
                                >
                                    Create FIX Session
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Status Bar */}
            <Box sx={{
                p: 2,
                borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                background: 'rgba(6, 182, 212, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#9c27b0' }}>⚙️</Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        LP: {lp?.name || 'Unknown'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: (fixSession && (fixSession.quoteSession || fixSession.tradeSession)) ? '#1de9b6' : '#ff6b6b' }}>
                        {(fixSession && (fixSession.quoteSession || fixSession.tradeSession)) ? '✅' : '❌'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        FIX Session: {(fixSession && (fixSession.quoteSession || fixSession.tradeSession)) ? 'Configured' : 'Not Configured'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>🕐</Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        {new Date().toLocaleTimeString()}
                    </Typography>
                </Box>
            </Box>

            {/* Child Modals */}
            <AddFIXSessionModal
                open={showAddFIXSession}
                onClose={() => setShowAddFIXSession(false)}
                onSuccess={handleAddFIXSessionSuccess}
                lp={lp}
            />

            <EditFIXSessionModal
                open={showEditFIXSession}
                onClose={() => setShowEditFIXSession(false)}
                onSuccess={handleEditFIXSessionSuccess}
                lp={lp}
                fixSession={fixSession}
            />
            </Box>
        </Modal>
    );
};

export default LPConfigurationModal;
